// src/pages/Leads.jsx
import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../styles/sideBar.css";

const STATUS_OPTIONS = [
  { value: "konwersja", label: "Konwersja" },
  { value: "odrzucono", label: "Odrzucono" },
  { value: "wstrzymano", label: "Wstrzymano" },
];

// Normalizujemy próby do dokładnie 3 slotów (stringi)
function normalizeAttempts(attempts) {
  if (Array.isArray(attempts)) {
    const a = attempts.map((x) => (typeof x === "string" ? x : "")).slice(0, 3);
    while (a.length < 3) a.push("");
    return a;
  }
  return ["", "", ""];
}

// Prosty “formatter” pod różne nazwy pól (żeby nie wywalało jak masz inne klucze)
function pickLeadField(lead, keys, fallback = "") {
  for (const k of keys) {
    const v = lead?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

export default function Leads({ isToogle }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // lokalny stan dla inputów prób (żeby nie pisać do firestore na każdy znak)
  const [attemptDraft, setAttemptDraft] = useState({}); // { [leadId]: "tekst" }
  const [saving, setSaving] = useState({}); // { [leadId]: boolean }
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");

    // Jeśli masz pole createdAt -> orderBy zadziała; jeśli nie masz, usuń query i daj collection(...)
    const q = query(collection(db, "leads"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLeads(rows);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setError("Nie udało się wczytać leadów.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  function formatFirestoreDate(value) {
  if (!value) return "-";

  try {
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString("pl-PL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    if (value instanceof Date) {
      return value.toLocaleString("pl-PL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return new Date(value).toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

  const rows = useMemo(() => {
    return leads.map((l) => {
      const attempts = normalizeAttempts(l.attempts);
      const nextAttemptIndex = attempts.findIndex((x) => !String(x || "").trim());
      return {
        ...l,
        attempts,
        nextAttemptIndex, // -1 jeśli wszystkie 3 zajęte
      };
    });
  }, [leads]);

  async function setStatus(leadId, statusValue) {
    try {
      setSaving((s) => ({ ...s, [leadId]: true }));
      await updateDoc(doc(db, "leads", leadId), {
        status: statusValue,
        statusUpdatedAt: new Date(),
      });
    } catch (e) {
      console.error(e);
      setError("Nie udało się zaktualizować statusu.");
    } finally {
      setSaving((s) => ({ ...s, [leadId]: false }));
    }
  }

  async function addAttempt(lead) {
    const leadId = lead.id;
    const draft = String(attemptDraft[leadId] || "").trim();
    if (!draft) return;

    const attempts = normalizeAttempts(lead.attempts);
    const idx = attempts.findIndex((x) => !String(x || "").trim());
    if (idx === -1) return; // już są 3 próby

    const next = [...attempts];
    next[idx] = draft;

    try {
      setSaving((s) => ({ ...s, [leadId]: true }));
      await updateDoc(doc(db, "leads", leadId), {
        attempts: next,
        lastAttemptAt: new Date(),
      });
      setAttemptDraft((d) => ({ ...d, [leadId]: "" }));
    } catch (e) {
      console.error(e);
      setError("Nie udało się dodać próby kontaktu.");
    } finally {
      setSaving((s) => ({ ...s, [leadId]: false }));
    }
  }

  return (
    <div
      className={isToogle ? "side-toogle-margin" : "side-margin"}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        minHeight: "100vh",
        overflowY: "auto",
        gap: "15px",
        paddingBottom: "20px",
        width: "100%",
      }}
    >
      <div style={{ width: "100%", maxWidth: 6500, padding: "15px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ margin: 0 }}>Leady</h2>
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {loading ? "Wczytywanie..." : `Łącznie: ${rows.length}`}
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(255,0,0,0.08)" }}>
            {error}
          </div>
        ) : null}

        <div style={{ width: "100%", overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={th}>Data leadu</th>
                <th style={th}>Imię i nazwisko</th>
                <th style={th}>Telefon</th>
                <th style={th}>Mail</th>
                <th style={th}>Wiek uczestnika</th>
                <th style={th}>Lokalizacja</th>
                <th style={th}>Typ zajęć</th>
                <th style={th}>Szczegóły</th>
                <th style={th}>Status</th>
                <th style={th}>Próby (max 3)</th>
              </tr>
            </thead>

            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td style={td} colSpan={10}>
                    Brak leadów.
                  </td>
                </tr>
              ) : null}

              {rows.map((lead) => {
                const createdAt = formatFirestoreDate(lead?.createdAt);
                const fullName = pickLeadField(lead, ["fullName", "name", "imieNazwisko", "clientName"]);
                const phone = pickLeadField(lead, ["phone", "telefon", "tel"]);
                const email = pickLeadField(lead, ["email", "mail"]);
                const age = pickLeadField(lead, ["age", "wiek", "participantAge"]);
                const location = pickLeadField(lead, ["locationName", "lokalizacja"]);
                const classType = pickLeadField(lead, ["classType", "type", "typZajec", "activityType"]);
                const details = pickLeadField(lead, ["details", "szczegoly", "note", "message"]);

                const statusValue = (lead.status || "wstrzymano").toLowerCase();
                const isBusy = !!saving[lead.id];

                return (
                  <tr key={lead.id} style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    <td style={td}>{createdAt}</td>
                    <td style={td}>{fullName}</td>
                    <td style={td}>{phone}</td>
                    <td style={td}>{email}</td>
                    <td style={td}>{age}</td>
                    <td style={td}>{location}</td>
                    <td style={td}>{classType}</td>
                    <td style={{ ...td, maxWidth: 3060, minWidth: 600 }}>
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {details}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* tip: jeśli nie masz createdAt w dokumentach, orderBy wywali błąd */}
        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>
          Jeśli nie masz pola <b>createdAt</b> w leadach, usuń <code>orderBy("createdAt","desc")</code> w query.
        </div>
      </div>
    </div>
  );
}

const th = {
  padding: "10px 10px",
  fontSize: 13,
  opacity: 0.8,
  borderBottom: "1px solid rgba(0,0,0,0.12)",
  whiteSpace: "nowrap",
};

const td = {
  padding: "10px 10px",
  fontSize: 14,
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const selectStyle = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.15)",
  outline: "none",
};

const inputStyle = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.15)",
  outline: "none",
  minWidth: 260,
};

const btnStyle = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.15)",
  background: "white",
  cursor: "pointer",
};
